import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import useDelete from '../../../components/hooks/useDelete';
import { delete_RdRentalAchInfo, getRdRentalACHInfoWithTenantId, put_RdRentalAchInfo } from '../../../services/ACHInfo';
import Pagination from '../../../components/Pagination';
import { useTheme } from '../../../components/hooks/useTheme';
import useTenant from '../../../components/hooks/useTenant';

const heading = ["#", "Account Number", "Account Type", "Name", "Routing Number", "Phone Number"];

type ACHDetailsProps = {
    isRefresh: boolean;
    setIsRefresh: (value: boolean) => void;
    setShowPopupAddNewACH: (value: boolean) => void;
}

const ACHDetails = ({
    isRefresh,
    setIsRefresh,
    setShowPopupAddNewACH
}: ACHDetailsProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles, theme } = useTheme();
    const { handleTenantError }: any = useTenant();
    const textColor =
        theme === "dark"
            ? `${themeStyles.main.textSubHeadingColor}`
            : `${themeStyles.main.textHeadingColor}`;

    const userId = Cookies.get("userId-jj-rental-ui");
    const { setDeletePopup, setData: setDeleteData }: any = useDelete();
    const [achData, setAChData] = useState<any[]>([]);
    const [paginationModel, setPaginationModel] = useState<{ page: number, size: number }>({
        page: 0,
        size: 5
    });
    const [totalLength, setTotalLength] = useState<number>(0);
    const [showEntries, setShowEntries] = useState<number>(5);

    useEffect(() => {
        if (isRefresh) {
            setIsRefresh(false);
            setPaginationModel({
                ...paginationModel,
                page: 0
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRefresh])

    useEffect(() => {
        if (tenantId) {
            getRdRentalACHInfoWithTenantId(tenantId, paginationModel.page, paginationModel.size)
                .then(res => {
                    setTotalLength(res?.count);
                    setAChData(res?.rdRentalACHInfo);
                    handleTenantError(null);
                })
                .catch(error => {
                    handleTenantError(error);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId, paginationModel])


    const addACH = () => setShowPopupAddNewACH(true);

    const deleteACH = (primaryKey: number) => {
        setDeleteData({
            id: primaryKey,
            tilte: `Delete`,
            summary: `Are you sure you want to delete this ACH Info?`,
            isDelete: (callback: any) => {
                if (userId)
                    delete_RdRentalAchInfo(primaryKey, userId?.toString())
                        .then(() => {
                            setIsRefresh(true);
                            handleTenantError(null);
                            callback();
                        })
                        .catch(error => {
                            handleTenantError(error);
                        })
            }
        });

        setDeletePopup(true);
    }

    const updateACH = (achDetails: any) => {
        put_RdRentalAchInfo(achDetails, Number(userId))
            .then(() => {
                setAChData(prevData =>
                    prevData.map(card =>
                        card.id === achDetails?.id
                            ? { ...card, isActive: achDetails?.isActive }
                            : card
                    )
                );
                handleTenantError(null);
            })
            .catch(error => {
                handleTenantError(error);
            })
    }

    return (
        <div className="w-full border border-[#167AC3] rounded-md">
            <div className="flex justify-between items-center w-full h-[50px] px-4 py-2 bg-[#167AC3] text-white rounded-tl-lg rounded-tr-lg">
                <h4 className="font-medium text-white">
                    ACH Info
                </h4>
            </div>

            <div className="flex flex-col p-4 gap-3">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className={`border-b ${themeStyles.main.borderColor} text-left text-sm ${textColor}`}>
                            {heading.map((item, index) => (
                                <th
                                    key={index}
                                    className={`border-r ${themeStyles.main.borderColor} text-left p-0.5 ${textColor}`}
                                >
                                    {item}
                                </th>
                            ))}
                            <th className={`text-start px-[0.125rem] ${textColor} font-bold`}></th>
                        </tr>
                    </thead>

                    <tbody>
                        {achData?.map((item: any, index: number) => (
                            <tr
                                key={index}
                                className={`text-left text-sm p-0.5 ${textColor}`}
                            >
                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    {item?.id}
                                </td>

                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    {item?.accountNumber}
                                </td>

                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    {item?.accountType}
                                </td>

                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    {item?.bankName}
                                </td>

                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    {item?.routingNumber}
                                </td>

                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    {item?.bankPhone}
                                </td>

                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    <button
                                        className={`p-2 rounded-md text-white ${item?.isActive ? 'bg-[#16a34a]' : 'bg-red-500'} cursor-pointer`}
                                        onClick={() => updateACH({ ...item, isActive: !item?.isActive })}
                                    >
                                        {
                                            item?.isActive
                                                ? "Yes"
                                                : "No"
                                        }
                                    </button>
                                </td>

                                <td
                                    className={`text-start border-b ${themeStyles.main.borderColor} px-[0.125rem] py-[0.375rem] font-bold cursor-pointer select-none ${textColor}`}
                                    onClick={() => deleteACH(item?.id)}
                                >
                                    {"X"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Pagination
                    totalLength={totalLength}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    setIsRefresh={setIsRefresh}
                    showEntries={showEntries}
                    setShowEntries={setShowEntries}
                />

                <div>
                    <button
                        className="bg-[#167AC3] rounded-md py-1 px-4 font-medium text-white cursor-pointer"
                        onClick={addACH}
                    >
                        Add New
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ACHDetails
